import { useCallback } from 'react';
import { useAnalyticsEnabled } from './useAnalytics';

// A discriminated union keyed by name: each event names its own param shape,
// so a typo'd event name or a missing param is a compile error rather than a
// silent gap in GA's DebugView after a deploy. Extend this union as real
// events exist, not ahead of them.
//
// **copy_prompt names the agent and the version and never the prompt.** What was
// copied is recoverable from those two, and the text itself is content nobody
// needs in an analytics property.
export type AnalyticsEvent =
  | { name: 'section_completed'; params: { section_id: string } }
  | { name: 'copy_prompt'; params: { agent_name: string; agent_version: string } };

// Fires a custom GA4 event. Safe to call from anywhere behind ProtectedLayout:
// it shares useAnalyticsEnabled with useAnalytics, so it never sends anything
// for a session useAnalytics itself would not have tracked, and it no-ops
// rather than throwing if gtag has not loaded yet.
export const useTrackEvent = (): ((event: AnalyticsEvent) => void) => {
  const enabled = useAnalyticsEnabled();

  return useCallback(
    (event: AnalyticsEvent) => {
      if (!enabled || window.gtag === undefined) {
        return;
      }
      window.gtag('event', event.name, event.params);
    },
    [enabled],
  );
};
